import React, { useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { PackageCard } from "./PackageCard";

const benefits = [
  "Access to all questions",
  "Scored all mock exams",
  "Unlimited AI scoring submissions for speaking",
  "Unlimited AI scoring submissions for writing",
  "Attempt all the mock exams",
  "Unlimited attempts per exam",
];

const plans = [
  { duration: 14 },
  { duration: 30, isPopular: true },
  { duration: 60 },
  { duration: 90 },
];

type PackageSelectionProps = {
  onCheckout: (days: number) => Promise<void>;
};

export const PackageSelection: React.FC<PackageSelectionProps> = ({
  onCheckout,
}) => {
  const [pendingDays, setPendingDays] = useState<number | null>(null);
  const [error, setError] = useState("");

  const startCheckout = async (days: number) => {
    setError("");
    setPendingDays(days);
    try {
      await onCheckout(days);
      // On success the browser is leaving for Stripe, so keep the spinner
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Checkout failed. Please try again.",
      );
      setPendingDays(null);
    }
  };

  return (
    <Box boxShadow="md" p={[5, 8]} rounded="2xl" bg="white">
      <Heading as="h1" size="xl" textAlign="center" mb={6}>
        Select Package
      </Heading>
      <Text mb={8} color="ink.muted" textAlign="center" maxW="3xl" mx="auto">
        Determined learners can select a budget-friendly Self-learner plan that
        aligns with their study timeframe. Enjoy unlimited AI scoring and
        recommendations in speaking and writing, graded mock exams, and full
        access to all mock exams to help you reach your goal with confidence.
      </Text>

      {error && (
        <Alert status="error" mb={6}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <SimpleGrid columns={[1, 2, null, 4]} spacing={5} mt={8}>
        {plans.map((plan) => (
          <PackageCard
            key={plan.duration}
            duration={plan.duration}
            benefits={benefits}
            isPopular={plan.isPopular}
            onCheckout={() => startCheckout(plan.duration)}
            isLoading={pendingDays === plan.duration}
            isDisabled={pendingDays !== null && pendingDays !== plan.duration}
          />
        ))}
      </SimpleGrid>

      <Text mt={8} fontSize="sm" color="ink.muted" textAlign="center">
        You'll be taken to Stripe's secure checkout to complete your payment.
      </Text>
    </Box>
  );
};
